import { useEffect, useMemo, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  KeyRound, MailboxIcon, ParkingSquare, Gift, PawPrint,
  Sparkles, CheckCircle2, AlertCircle, User, RotateCcw, PlayCircle,
} from "lucide-react";

/**
 * MoveInChecklistPage — one admin click that runs 5 typically-sequential
 * new-resident tasks (fob, mailbox, parking, welcome package, pet) in a
 * single API call. Every step is optional and independently toggleable.
 */

const COMMON_ZONES = [
  "Main Gate", "Pool", "Gym", "Clubhouse", "Package Room", "Garage",
  "Mail Room", "Elevator", "Bike Room", "Amenity Deck",
];

const DEFAULT_FORM = () => ({
  resident: { existingId: "", email: "", name: "", phone: "", unit: "" },
  steps: {
    access: { enabled: true,  type: "fob", credential_id: "", zones: ["Main Gate", "Pool", "Package Room"], notes: "" },
    mailbox: { enabled: true, box_number: "", key_count: 2, notes: "" },
    parking: { enabled: false, spot: "", plate: "", vehicle: "" },
    welcome_package: { enabled: true, carrier: "Community", tracking: "", locker: "", notes: "Welcome kit — leasing packet + keys + FAQ card", notify: true },
    pet: { enabled: false, pet_name: "", species: "dog", breed: "", weight_lbs: "", approve_immediately: true },
  },
});

const STEP_META = {
  access: { icon: KeyRound, label: "Access credential (fob / mobile pass)", accent: "text-orange-300" },
  mailbox: { icon: MailboxIcon, label: "Mailbox assignment", accent: "text-sky-300" },
  parking: { icon: ParkingSquare, label: "Parking spot", accent: "text-emerald-300" },
  welcome_package: { icon: Gift, label: "Welcome package", accent: "text-fuchsia-300" },
  pet: { icon: PawPrint, label: "Pet registration", accent: "text-amber-300" },
};

export default function MoveInChecklistPage() {
  const { propertyId } = useProperty();
  const [form, setForm] = useState(DEFAULT_FORM());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [residents, setResidents] = useState([]);
  const [history, setHistory] = useState([]);

  // Load residents for the existing-resident picker + recent history
  useEffect(() => {
    if (!propertyId) return;
    (async () => {
      try {
        const [r, h] = await Promise.all([
          api.get(`/admin/residents?property_id=${propertyId}`),
          api.get(`/admin/move-in/history?property_id=${propertyId}&limit=8`).catch(() => ({ data: { items: [] } })),
        ]);
        setResidents(r.data.items || []);
        setHistory(h.data.items || []);
      } catch (err) { if (typeof console !== 'undefined') console.debug('pages/admin/MoveInChecklistPage.jsx: swallowed', err); }
    })();
  }, [propertyId, result]);

  const pickResident = (id) => {
    const r = residents.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      resident: {
        existingId: r?.id || "",
        email: r?.email || "",
        name: r?.name || "",
        phone: r?.phone || "",
        unit: r?.unit || "",
      },
    }));
  };
  const toggleStep = (key, enabled) => {
    setForm((f) => ({ ...f, steps: { ...f.steps, [key]: { ...f.steps[key], enabled } } }));
  };
  const setStepField = (key, field, value) => {
    setForm((f) => ({ ...f, steps: { ...f.steps, [key]: { ...f.steps[key], [field]: value } } }));
  };
  const toggleZone = (zone) => {
    setForm((f) => {
      const cur = f.steps.access.zones;
      const next = cur.includes(zone) ? cur.filter((z) => z !== zone) : [...cur, zone];
      return { ...f, steps: { ...f.steps, access: { ...f.steps.access, zones: next } } };
    });
  };

  const reset = () => { setForm(DEFAULT_FORM()); setResult(null); };

  const canRun = useMemo(() => {
    const r = form.resident;
    if (!r.unit.trim()) return false;
    if (!r.existingId && (!r.email.trim() || !r.name.trim())) return false;
    // At least one step must be enabled
    return Object.values(form.steps).some((s) => s.enabled);
  }, [form]);

  const buildPayload = () => {
    const payload = {
      property_id: propertyId,
      resident: {
        resident_id: form.resident.existingId || null,
        email: form.resident.email.trim() || null,
        name: form.resident.name.trim() || null,
        phone: form.resident.phone.trim() || null,
        unit: form.resident.unit.trim(),
      },
    };
    if (form.steps.access.enabled) {
      const a = form.steps.access;
      payload.access = {
        type: a.type,
        credential_id: a.credential_id.trim(),
        zones: a.zones,
        notes: a.notes.trim() || null,
      };
    }
    if (form.steps.mailbox.enabled) {
      const m = form.steps.mailbox;
      payload.mailbox = {
        box_number: m.box_number.trim(),
        key_count: Number(m.key_count) || 2,
        notes: m.notes.trim() || null,
      };
    }
    if (form.steps.parking.enabled) {
      const p = form.steps.parking;
      payload.parking = {
        spot: p.spot.trim(),
        plate: p.plate.trim(),
        vehicle: p.vehicle.trim(),
      };
    }
    if (form.steps.welcome_package.enabled) {
      const w = form.steps.welcome_package;
      payload.welcome_package = {
        carrier: w.carrier.trim() || "Community",
        tracking: w.tracking.trim() || `WELCOME-${form.resident.unit.trim()}-${Date.now().toString(36).slice(-4).toUpperCase()}`,
        locker: w.locker.trim() || null,
        notes: w.notes.trim() || null,
        notify: !!w.notify,
      };
    }
    if (form.steps.pet.enabled) {
      const p = form.steps.pet;
      payload.pet = {
        pet_name: p.pet_name.trim(),
        species: p.species,
        breed: p.breed.trim() || null,
        weight_lbs: p.weight_lbs ? Number(p.weight_lbs) : null,
        approve_immediately: !!p.approve_immediately,
      };
    }
    return payload;
  };

  const run = async () => {
    if (!canRun) return toast.error("Fill unit + at least one step");
    // Basic per-step validation
    if (form.steps.access.enabled && !form.steps.access.credential_id.trim()) {
      return toast.error("Access step: credential ID is required");
    }
    if (form.steps.mailbox.enabled && !form.steps.mailbox.box_number.trim()) {
      return toast.error("Mailbox step: box number is required");
    }
    if (form.steps.parking.enabled && (!form.steps.parking.spot.trim() || !form.steps.parking.plate.trim() || !form.steps.parking.vehicle.trim())) {
      return toast.error("Parking step: spot, plate, and vehicle are all required");
    }
    if (form.steps.pet.enabled && !form.steps.pet.pet_name.trim()) {
      return toast.error("Pet step: pet name is required");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/move-in/checklist", buildPayload());
      setResult(data);
      const s = data.summary || {};
      toast.success(`Move-in complete — ${s.created || 0} created${s.skipped ? `, ${s.skipped} skipped` : ""}`);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader
        eyebrow="Operations"
        title="Move-in Checklist"
        subtitle="One click. Five tasks done. Fob issued, mailbox handed off, spot assigned, welcome package logged, pet registered — every row stamped with the same move-in ID for a clean audit trail."
        actions={
          <div className="flex items-center gap-2">
            {result && (
              <Button variant="outline" onClick={reset} className="gap-2 border-white/10" data-testid="movein-reset">
                <RotateCcw className="h-4 w-4" /> New move-in
              </Button>
            )}
            <Button onClick={run} disabled={!canRun || busy}
                    className="bg-orange-500 hover:bg-orange-600 gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid="movein-run">
              <PlayCircle className="h-4 w-4" />
              {busy ? "Running…" : "Complete move-in"}
            </Button>
          </div>
        }
      />

      {result && <ResultSummary result={result} />}

      {!result && (
        <>
          {/* Resident block */}
          <Card className="border-white/10 bg-card p-5 mb-4" data-testid="movein-resident-card">
            <div className="flex items-center gap-2 mb-3">
              <User className="h-4 w-4 text-orange-300" />
              <div className="font-semibold text-sm">Resident</div>
              <Badge variant="outline" className="border-white/10 text-[10px]">
                {form.resident.existingId ? "Existing resident" : "New resident (auto-provisioned)"}
              </Badge>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Existing resident (optional)</Label>
                <Select value={form.resident.existingId || "__new"} onValueChange={(v) => v === "__new" ? setForm((f) => ({ ...f, resident: { ...f.resident, existingId: "" } })) : pickResident(v)}>
                  <SelectTrigger data-testid="movein-resident-picker" className="bg-white/[0.02] border-white/10">
                    <SelectValue placeholder="Pick or leave blank to create new" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    <SelectItem value="__new">— Create new resident —</SelectItem>
                    {residents.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name} · Unit {r.unit || "—"} · {r.email}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Unit *</Label>
                <Input value={form.resident.unit}
                       onChange={(e) => setForm((f) => ({ ...f, resident: { ...f.resident, unit: e.target.value } }))}
                       placeholder="4E" data-testid="movein-unit" />
              </div>
              {!form.resident.existingId && (
                <>
                  <div>
                    <Label className="text-xs">Email *</Label>
                    <Input type="email" value={form.resident.email}
                           onChange={(e) => setForm((f) => ({ ...f, resident: { ...f.resident, email: e.target.value } }))}
                           placeholder="jane@example.com" data-testid="movein-email" />
                  </div>
                  <div>
                    <Label className="text-xs">Full name *</Label>
                    <Input value={form.resident.name}
                           onChange={(e) => setForm((f) => ({ ...f, resident: { ...f.resident, name: e.target.value } }))}
                           placeholder="Jane Smith" data-testid="movein-name" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Phone (optional)</Label>
                    <Input value={form.resident.phone}
                           onChange={(e) => setForm((f) => ({ ...f, resident: { ...f.resident, phone: e.target.value } }))}
                           placeholder="+1 555-010-1234" />
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* Access step */}
          <StepCard k="access" enabled={form.steps.access.enabled} onToggle={(v) => toggleStep("access", v)}>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={form.steps.access.type} onValueChange={(v) => setStepField("access", "type", v)}>
                  <SelectTrigger data-testid="movein-access-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fob">Fob</SelectItem>
                    <SelectItem value="mobile_pass">Mobile pass</SelectItem>
                    <SelectItem value="pin">PIN code</SelectItem>
                    <SelectItem value="key">Physical key</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Credential ID *</Label>
                <Input value={form.steps.access.credential_id}
                       onChange={(e) => setStepField("access", "credential_id", e.target.value)}
                       placeholder="FOB-2201" data-testid="movein-access-id" />
              </div>
            </div>
            <div className="mt-3">
              <Label className="text-xs">Zones</Label>
              <div className="flex flex-wrap gap-1 mt-2">
                {COMMON_ZONES.map((z) => (
                  <button key={z} type="button" onClick={() => toggleZone(z)}
                          data-testid={`movein-zone-${z.replace(/\s+/g, "-").toLowerCase()}`}
                          className={"text-[11px] px-2 py-1 rounded-md border transition-colors " +
                            (form.steps.access.zones.includes(z)
                              ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-300"
                              : "border-white/10 text-zinc-400 hover:bg-white/5")}>
                    {z}
                  </button>
                ))}
              </div>
            </div>
          </StepCard>

          {/* Mailbox step */}
          <StepCard k="mailbox" enabled={form.steps.mailbox.enabled} onToggle={(v) => toggleStep("mailbox", v)}>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Box number *</Label>
                <Input value={form.steps.mailbox.box_number}
                       onChange={(e) => setStepField("mailbox", "box_number", e.target.value)}
                       placeholder="MB-4E" data-testid="movein-mailbox-box" />
              </div>
              <div>
                <Label className="text-xs">Keys issued</Label>
                <Input type="number" min="0" max="10" value={form.steps.mailbox.key_count}
                       onChange={(e) => setStepField("mailbox", "key_count", e.target.value)}
                       data-testid="movein-mailbox-keys" />
              </div>
            </div>
          </StepCard>

          {/* Parking step */}
          <StepCard k="parking" enabled={form.steps.parking.enabled} onToggle={(v) => toggleStep("parking", v)}>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Spot *</Label>
                <Input value={form.steps.parking.spot}
                       onChange={(e) => setStepField("parking", "spot", e.target.value)}
                       placeholder="G-27" data-testid="movein-parking-spot" />
              </div>
              <div>
                <Label className="text-xs">Plate *</Label>
                <Input value={form.steps.parking.plate}
                       onChange={(e) => setStepField("parking", "plate", e.target.value)}
                       placeholder="ABC-1234" data-testid="movein-parking-plate" />
              </div>
              <div>
                <Label className="text-xs">Vehicle *</Label>
                <Input value={form.steps.parking.vehicle}
                       onChange={(e) => setStepField("parking", "vehicle", e.target.value)}
                       placeholder="2023 Tesla Model 3" data-testid="movein-parking-vehicle" />
              </div>
            </div>
          </StepCard>

          {/* Welcome package step */}
          <StepCard k="welcome_package" enabled={form.steps.welcome_package.enabled} onToggle={(v) => toggleStep("welcome_package", v)}>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Carrier / source</Label>
                <Input value={form.steps.welcome_package.carrier}
                       onChange={(e) => setStepField("welcome_package", "carrier", e.target.value)}
                       placeholder="Community" data-testid="movein-package-carrier" />
              </div>
              <div>
                <Label className="text-xs">Tracking / short ID</Label>
                <Input value={form.steps.welcome_package.tracking}
                       onChange={(e) => setStepField("welcome_package", "tracking", e.target.value)}
                       placeholder="auto-generated if blank" data-testid="movein-package-tracking" />
              </div>
              <div>
                <Label className="text-xs">Locker / shelf</Label>
                <Input value={form.steps.welcome_package.locker}
                       onChange={(e) => setStepField("welcome_package", "locker", e.target.value)}
                       placeholder="Front desk hand-off" data-testid="movein-package-locker" />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                  <Switch checked={form.steps.welcome_package.notify}
                          onCheckedChange={(v) => setStepField("welcome_package", "notify", v)}
                          data-testid="movein-package-notify" />
                  Fire arrival notification (push + email + SMS)
                </label>
              </div>
              <div className="col-span-2">
                <Label className="text-xs">Notes</Label>
                <Textarea rows={2} value={form.steps.welcome_package.notes}
                          onChange={(e) => setStepField("welcome_package", "notes", e.target.value)} />
              </div>
            </div>
          </StepCard>

          {/* Pet step */}
          <StepCard k="pet" enabled={form.steps.pet.enabled} onToggle={(v) => toggleStep("pet", v)}>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Pet name *</Label>
                <Input value={form.steps.pet.pet_name}
                       onChange={(e) => setStepField("pet", "pet_name", e.target.value)}
                       placeholder="Bella" data-testid="movein-pet-name" />
              </div>
              <div>
                <Label className="text-xs">Species</Label>
                <Select value={form.steps.pet.species} onValueChange={(v) => setStepField("pet", "species", v)}>
                  <SelectTrigger data-testid="movein-pet-species"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dog">Dog</SelectItem>
                    <SelectItem value="cat">Cat</SelectItem>
                    <SelectItem value="bird">Bird</SelectItem>
                    <SelectItem value="fish">Fish</SelectItem>
                    <SelectItem value="reptile">Reptile</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Breed</Label>
                <Input value={form.steps.pet.breed}
                       onChange={(e) => setStepField("pet", "breed", e.target.value)}
                       placeholder="Golden Retriever" />
              </div>
              <div>
                <Label className="text-xs">Weight (lbs)</Label>
                <Input type="number" value={form.steps.pet.weight_lbs}
                       onChange={(e) => setStepField("pet", "weight_lbs", e.target.value)}
                       placeholder="45" />
              </div>
              <div className="col-span-2">
                <label className="flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                  <Switch checked={form.steps.pet.approve_immediately}
                          onCheckedChange={(v) => setStepField("pet", "approve_immediately", v)}
                          data-testid="movein-pet-preapprove" />
                  Approve immediately (skip pending review)
                </label>
              </div>
            </div>
          </StepCard>

          {history.length > 0 && (
            <Card className="border-white/10 bg-card p-4 mt-6" data-testid="movein-history">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">
                Recent move-ins ({history.length})
              </div>
              <div className="space-y-2 text-[12px]">
                {history.map((h) => (
                  <div key={h.move_in_id} className="flex items-center justify-between border border-white/5 rounded-md px-2 py-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{h.resident_name} · Unit {h.unit}</div>
                      <div className="text-[10px] text-zinc-500 truncate">
                        {new Date(h.created_at).toLocaleString()} · fob {h.credential_id}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      {Object.entries(h.item_counts || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                        <Badge key={k} variant="outline" className="text-[10px] border-white/10">
                          {k} · {n}
                        </Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}

function StepCard({ k, enabled, onToggle, children }) {
  const meta = STEP_META[k];
  const Icon = meta.icon;
  return (
    <Card className={"border-white/10 bg-card p-4 mb-3 transition-opacity " + (enabled ? "" : "opacity-50")} data-testid={`movein-step-${k}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className={"h-4 w-4 " + meta.accent} />
          <div className="font-semibold text-sm">{meta.label}</div>
        </div>
        <Switch checked={enabled} onCheckedChange={onToggle} data-testid={`movein-step-toggle-${k}`} />
      </div>
      {enabled && children}
    </Card>
  );
}

function ResultSummary({ result }) {
  const steps = result.steps || {};
  return (
    <Card className="border-emerald-500/30 bg-emerald-500/[0.02] p-5 mb-4" data-testid="movein-result">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="h-5 w-5 text-emerald-400" />
        <div className="font-semibold">Move-in complete</div>
        <Badge variant="outline" className="border-emerald-500/40 text-emerald-300">
          {result.summary?.created || 0} of {result.summary?.attempted || 0} landed
        </Badge>
        {result.summary?.skipped > 0 && (
          <Badge variant="outline" className="border-amber-500/40 text-amber-300">
            {result.summary.skipped} skipped
          </Badge>
        )}
      </div>
      <div className="text-[12px] text-zinc-400 mb-3">
        Move-in ID <span className="font-mono">{result.move_in_id}</span> · Resident{" "}
        <span className="font-semibold text-white">{result.resident?.name}</span>
        {result.resident?.newly_created && (
          <Badge variant="outline" className="ml-2 border-orange-500/40 text-orange-300 text-[10px]">
            new account provisioned
          </Badge>
        )}
      </div>
      <div className="grid md:grid-cols-2 gap-2">
        {Object.entries(steps).map(([k, s]) => {
          const meta = STEP_META[k] || { icon: CheckCircle2, label: k };
          const Icon = meta.icon;
          return (
            <div key={k} className={"flex items-start gap-2 border rounded-md p-2 " +
              (s.ok ? "border-emerald-500/30 bg-emerald-500/[0.04]"
                    : "border-amber-500/30 bg-amber-500/[0.04]")}
                 data-testid={`movein-result-${k}`}>
              {s.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                    : <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1">
                  <Icon className={"h-3 w-3 " + meta.accent} />
                  <div className="text-[12px] font-medium">{meta.label}</div>
                </div>
                <div className="text-[11px] text-zinc-500 truncate">
                  {s.ok
                    ? `Created${s.credential_id ? " · " + s.credential_id : ""}${s.box_number ? " · " + s.box_number : ""}${s.spot ? " · " + s.spot : ""}${s.tracking ? " · " + s.tracking : ""}${s.pet_name ? " · " + s.pet_name : ""}`
                    : `Skipped — ${s.reason || "already existed"}`}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
